import {
  deleteCoachNotification,
  fetchCoachNotifications,
  markAllCoachNotificationsRead,
  markCoachNotificationRead,
} from '@/features/coach/services/notifications';
import { useSessionStore } from '@/stores';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';

import type { CoachNotificationsResponse } from '../notifications/types';
import { coachKeys } from './query-keys';

const NOTIFICATIONS_PAGE_SIZE = 20;

type NotificationsCache = InfiniteData<CoachNotificationsResponse, string | undefined>;

export function useCoachNotificationsQuery() {
  const coachId = useSessionStore(state => state.user?.uid);

  return useInfiniteQuery({
    queryKey: coachKeys.notifications(coachId),
    queryFn: ({ pageParam }) =>
      fetchCoachNotifications({
        limit: NOTIFICATIONS_PAGE_SIZE,
        cursor: pageParam,
        paginate: true,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
    enabled: Boolean(coachId),
    // Read/delete state must always reflect the server when the screen is opened.
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

function updateNotificationPages(
  cache: NotificationsCache | undefined,
  update: (page: CoachNotificationsResponse) => CoachNotificationsResponse,
) {
  return cache ? { ...cache, pages: cache.pages.map(update) } : cache;
}

function useNotificationMutationCache() {
  const coachId = useSessionStore(state => state.user?.uid);
  const queryClient = useQueryClient();
  const queryKey = coachKeys.notifications(coachId);

  return { queryClient, queryKey };
}

/** Marks one notification read, applied optimistically so the unread dot clears on tap. */
export function useMarkCoachNotificationReadMutation() {
  const { queryClient, queryKey } = useNotificationMutationCache();

  return useMutation({
    mutationFn: markCoachNotificationRead,
    onMutate: async id => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<NotificationsCache>(queryKey);
      const target = previous?.pages
        .flatMap(page => page.notifications)
        .find(item => item.id === id);

      queryClient.setQueryData<NotificationsCache>(queryKey, cache =>
        updateNotificationPages(cache, page => {
          if (!target || target.read) return page;
          return {
            ...page,
            unreadCount: Math.max(0, page.unreadCount - 1),
            notifications: page.notifications.map(item =>
              item.id === id ? { ...item, read: true } : item,
            ),
          };
        }),
      );
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
}

/** Marks every notification read (settings sheet). */
export function useMarkAllCoachNotificationsReadMutation() {
  const { queryClient, queryKey } = useNotificationMutationCache();

  return useMutation({
    mutationFn: markAllCoachNotificationsRead,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<NotificationsCache>(queryKey);

      queryClient.setQueryData<NotificationsCache>(queryKey, cache =>
        updateNotificationPages(cache, page => ({
          ...page,
          unreadCount: 0,
          notifications: page.notifications.map(item => ({ ...item, read: true })),
        })),
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
}

/** Deletes one notification. BR-02: no confirmation, so the row is removed optimistically and
 *  only restored if the request fails. */
export function useDeleteCoachNotificationMutation() {
  const { queryClient, queryKey } = useNotificationMutationCache();

  return useMutation({
    mutationFn: deleteCoachNotification,
    onMutate: async id => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<NotificationsCache>(queryKey);
      const target = previous?.pages
        .flatMap(page => page.notifications)
        .find(item => item.id === id);

      queryClient.setQueryData<NotificationsCache>(queryKey, cache =>
        updateNotificationPages(cache, page => ({
          ...page,
          unreadCount:
            target && !target.read ? Math.max(0, page.unreadCount - 1) : page.unreadCount,
          notifications: page.notifications.filter(item => item.id !== id),
        })),
      );
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
}
